/**
 * Format a decimal value as a percentage with optional precision.
 * Removes trailing zeros to match d3-format behavior.
 *
 * @param value - The decimal value to format (e.g., 0.98 for 98%)
 * @param precision - Number of decimal places (default: 1)
 * @returns Formatted percentage string (e.g., "98%" or "<0.1%")
 *
 * @example
 * formatPercent(0.98, 1) // "98%"
 * formatPercent(0.981, 1) // "98.1%"
 * formatPercent(0.0005, 1) // "<0.1%"
 * formatPercent(0.00005, 2) // "<0.01%"
 */
export function formatPercent(value: number, precision = 1): string {
  const percent = value * 100;
  const threshold = Math.pow(10, -precision);

  // Handle values that round to 0 but aren't actually 0
  if (percent > 0 && percent < threshold) {
    // Format the threshold without trailing zeros
    const formattedThreshold = Number(threshold.toFixed(precision)).toString();
    return `<${formattedThreshold}%`;
  }

  // Format and remove trailing zeros
  const formatted = percent.toFixed(precision);
  const withoutTrailingZeros = Number(formatted).toString();
  return `${withoutTrailingZeros}%`;
}

export function formatDecimal(value: number, decimalPlaces = 2): string {
  return String(Math.round(value * 10 ** decimalPlaces) / 10 ** decimalPlaces);
}

export type FormatNumberOptions = {
  locale?: string | string[];
  round?: boolean;
  fallback?: string;
};

/**
 * Format a number with thousands separators.
 *
 * @param value - The number or numeric string to format
 * @param options - Optional locale, rounding, and fallback
 * @returns Formatted number string (e.g., "1,234,567")
 *
 * @example
 * formatNumber(1234567) // "1,234,567"
 * formatNumber(1000) // "1,000"
 * formatNumber(undefined) // "0"
 * formatNumber(undefined, { fallback: '-' }) // "-"
 * formatNumber(1234.6, { round: true }) // "1,235"
 * formatNumber(1234, { locale: 'de-DE' }) // "1.234"
 */
export function formatNumber(value?: number | string, options: FormatNumberOptions = {}): string {
  const { locale, round = false, fallback = '0' } = options;
  if (value == null) return fallback;

  const normalized = Number(value);
  if (Number.isNaN(normalized)) return String(value);

  const rounded = round ? Math.round(normalized) : normalized;
  return new Intl.NumberFormat(locale).format(rounded);
}

/**
 * Format a number in compact notation (e.g. "1.2K", "3M"), matching the Ember
 * `compactNumber` helper.
 *
 * @example
 * formatCompactNumber(1234) // "1K"
 * formatCompactNumber(50) // "50"
 */
export function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 0 }).format(value);
}
